import { useEffect, useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileUp,
  GripVertical,
  Info,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useClearEmployeeProfileFieldValue,
  useSetEmployeeProfileFieldValue,
  useUploadEmployeeProfileFieldFile,
} from '@/hooks/use-employee-profile-block-fields';
import type { EmployeeProfileBlockField } from '@hygge/shared';

interface CustomFieldRowProps {
  employeeId: string;
  field: EmployeeProfileBlockField;
  value: string | null;
  canCustomize: boolean;
  canEditValue: boolean;
  onEditField: () => void;
  onDeleteField: () => void;
}

function extractApiError(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string | string[] }> | undefined;
  const data = axiosError?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return fallback;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00`);
  return Number.isNaN(ts) ? null : new Date(ts);
}

function formatDateForApi(date: Date | null): string | null {
  if (!date) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getFileName(value: string | null): string | null {
  if (!value) return null;
  try {
    const decoded = decodeURIComponent(value.split('/').pop() ?? '');
    return decoded || null;
  } catch {
    return null;
  }
}

export function CustomFieldRow({
  employeeId,
  field,
  value,
  canCustomize,
  canEditValue,
  onEditField,
  onDeleteField,
}: CustomFieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled: !canCustomize,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const setMutation = useSetEmployeeProfileFieldValue(employeeId);
  const clearMutation = useClearEmployeeProfileFieldValue(employeeId);
  const uploadMutation = useUploadEmployeeProfileFieldFile(employeeId);

  const persist = (next: string) => {
    setMutation.mutate(
      { fieldId: field.id, value: next },
      { onError: (err) => toast.error(extractApiError(err, 'Failed to save value')) },
    );
  };

  const clear = () => {
    clearMutation.mutate(field.id, {
      onError: (err) => toast.error(extractApiError(err, 'Failed to clear value')),
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group grid gap-1 rounded-md px-2 py-2 -mx-2 hover:bg-muted/40"
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {canCustomize && (
          <button
            type="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing touch-none transition-opacity"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="flex flex-1 items-center gap-1 min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
            {field.name}
          </span>
          {field.required && (
            <span className="text-[10px] font-semibold text-destructive" aria-label="Required">
              *
            </span>
          )}
          {field.description && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-muted-foreground/70 hover:text-foreground">
                    <Info className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">{field.description}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {canCustomize && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Field actions"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onSelect={onEditField}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit field
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDeleteField} className="text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete field
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <FieldValueInput
        field={field}
        value={value}
        disabled={!canEditValue}
        isSaving={setMutation.isPending || uploadMutation.isPending || clearMutation.isPending}
        onCommit={persist}
        onClear={clear}
        onUploadFile={(file) =>
          uploadMutation.mutate(
            { fieldId: field.id, file },
            { onError: (err) => toast.error(extractApiError(err, 'Failed to upload file')) },
          )
        }
      />
    </div>
  );
}

interface FieldValueInputProps {
  field: EmployeeProfileBlockField;
  value: string | null;
  disabled: boolean;
  isSaving: boolean;
  onCommit: (next: string) => void;
  onClear: () => void;
  onUploadFile: (file: File) => void;
}

function FieldValueInput({
  field,
  value,
  disabled,
  isSaving,
  onCommit,
  onClear,
  onUploadFile,
}: FieldValueInputProps) {
  const [draft, setDraft] = useState(value ?? '');
  const lastSyncedValue = useRef(value);

  useEffect(() => {
    if (lastSyncedValue.current !== value) {
      lastSyncedValue.current = value;
      setDraft(value ?? '');
    }
  }, [value]);

  const commitText = (next: string) => {
    const trimmed = next.trim();
    if (trimmed === (value ?? '')) return;
    if (trimmed === '') {
      onClear();
    } else {
      onCommit(trimmed);
    }
  };

  switch (field.type) {
    case 'LONG_TEXT':
      return (
        <Textarea
          rows={2}
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : 'Empty'}
          className="text-xs"
        />
      );

    case 'NUMBER':
      return (
        <Input
          type="number"
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : 'Empty'}
          className="h-8 text-xs"
        />
      );

    case 'EMAIL':
      return (
        <Input
          type="email"
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : 'name@example.com'}
          className="h-8 text-xs"
        />
      );

    case 'PHONE':
      return (
        <Input
          type="tel"
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : '+1 555 0100'}
          className="h-8 text-xs"
        />
      );

    case 'URL':
      return (
        <Input
          type="url"
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : 'https://example.com'}
          className="h-8 text-xs"
        />
      );

    case 'DATE': {
      const parsed = parseDate(value);
      return (
        <DatePicker
          value={parsed}
          disabled={disabled}
          onChange={(date) => {
            const iso = formatDateForApi(date);
            if (iso === null) {
              onClear();
            } else if (iso !== value) {
              onCommit(iso);
            }
          }}
          className="text-xs"
        />
      );
    }

    case 'DROPDOWN':
      return (
        <Select
          disabled={disabled}
          value={value ?? ''}
          onValueChange={(next) => {
            if (next === '__clear__') {
              onClear();
              return;
            }
            if (next && next !== value) onCommit(next);
          }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder={disabled ? '' : 'Choose…'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
            {value && !disabled && (
              <SelectItem value="__clear__" className="text-muted-foreground">
                Clear selection
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      );

    case 'CHECKBOX':
      return (
        <div className="flex items-center gap-2">
          <Switch
            disabled={disabled}
            checked={value === 'true'}
            onCheckedChange={(next) => onCommit(next ? 'true' : 'false')}
          />
          <span className="text-xs text-muted-foreground">
            {value === 'true' ? 'Yes' : 'No'}
          </span>
        </div>
      );

    case 'FILE': {
      const fileName = getFileName(value);
      return (
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <a
                href={value}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline truncate"
              >
                {fileName ?? 'Attached file'}
              </a>
              {!disabled && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={onClear}
                  aria-label="Remove file"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <span className="text-xs text-muted-foreground italic">No file</span>
          )}
          {!disabled && (
            <label
              className={cn(
                'inline-flex items-center gap-1 text-[11px] cursor-pointer rounded border border-dashed border-border/70 px-2 py-1 text-muted-foreground hover:text-foreground hover:border-foreground/40',
                isSaving && 'opacity-50 pointer-events-none',
              )}
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <FileUp className="h-3 w-3" />
              )}
              <span>{value ? 'Replace' : 'Upload'}</span>
              <input
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onUploadFile(f);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>
      );
    }

    case 'SHORT_TEXT':
    default:
      return (
        <Input
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitText(draft)}
          placeholder={disabled ? '' : 'Empty'}
          className="h-8 text-xs"
        />
      );
  }
}

